"use client"

import { useState, useEffect } from "react"

interface PublishManagerProps {
  selectedLanguage: string
  onClose: () => void
}

export function PublishManager({ selectedLanguage, onClose }: PublishManagerProps) {
  const [allLessons, setAllLessons] = useState<string[]>([])
  const [publishedLessons, setPublishedLessons] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)

  useEffect(() => {
    loadData()
  }, [selectedLanguage])

  const loadData = async () => {
    setLoading(true)
    try {
      // Load all available lessons
      const listResponse = await fetch(`/portal/lesson-admin-api.php?action=list&language=${selectedLanguage}`)
      const listData = await listResponse.json()

      if (!listData.success) {
        throw new Error(listData.error || "Failed to load lessons")
      }

      // Load published lessons from directory.json
      const dirResponse = await fetch(`/portal/lesson-admin-api.php?action=getDirectory&language=${selectedLanguage}`)
      const dirData = await dirResponse.json()

      if (!dirData.success) {
        throw new Error(dirData.error || "Failed to load directory")
      }

      setAllLessons(listData.lessons || [])
      setPublishedLessons(dirData.published || [])
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to load data",
      })
    } finally {
      setLoading(false)
    }
  }

  const togglePublish = (lessonId: string) => {
    setPublishedLessons((prev) => {
      if (prev.includes(lessonId)) {
        return prev.filter((id) => id !== lessonId)
      } else {
        return [...prev, lessonId]
      }
    })
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)

    try {
      const response = await fetch("/portal/lesson-admin-api.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "updateDirectory",
          language: selectedLanguage,
          published: publishedLessons,
        }),
      })

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to update directory")
      }

      setMessage({ type: "success", text: "Published lessons updated successfully!" })
    } catch (error) {
      setMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to save",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Manage Published Lessons</h2>
            <button className="modal-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="modal-body">
            <p>Loading lessons...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content publish-manager-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Published Lessons</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            Select which lessons should be visible on the live site. Only published lessons will appear in the
            directory.
          </p>

          {message && <div className={`message message-${message.type}`}>{message.text}</div>}

          <div className="publish-list">
            {allLessons.length === 0 ? (
              <p className="empty-state">No lessons found for {selectedLanguage}</p>
            ) : (
              allLessons.map((lessonId) => {
                const isPublished = publishedLessons.includes(lessonId)
                return (
                  <div key={lessonId} className="publish-item">
                    <label className="publish-checkbox">
                      <input type="checkbox" checked={isPublished} onChange={() => togglePublish(lessonId)} />
                      <span className="lesson-id">{lessonId}</span>
                    </label>
                    <span className={`publish-status ${isPublished ? "published" : "unpublished"}`}>
                      {isPublished ? "Published" : "Unpublished"}
                    </span>
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  )
}
